import { DEPARTMENT_BY_SERVICE_CATEGORY } from '@jira-clone/shared-types'

export interface ServiceCategory {
  category: string
  color: string
  badgeClass: string
  departmentCode: string
  departmentLabel: string
  services: string[]
}

export const SERVICE_CATEGORIES: ServiceCategory[] = [
  {
    category: 'TECHNOLOGY',
    color: '#6366f1',
    badgeClass: 'bg-indigo-500/10 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800',
    departmentCode: 'technology',
    departmentLabel: 'Development',
    services: [
      'Web Development',
      'Mobile App Development',
      'Custom Software Development',
      'AI / ML',
      'Cybersecurity',
      'Cloud Services',
      'IT Consulting',
    ],
  },
  {
    category: 'MARKETING & GROWTH',
    color: '#f59e0b',
    badgeClass: 'bg-amber-500/10 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800',
    departmentCode: 'marketing_growth',
    departmentLabel: 'Marketing & Growth',
    services: [
      'Digital Marketing',
      'Performance Marketing',
      'Marketplace Product Marketing',
      'Advertising',
      'Political Campaign Marketing',
      'Bulk Communication Services',
    ],
  },
  {
    category: 'CREATIVE',
    color: '#ec4899',
    badgeClass: 'bg-pink-500/10 text-pink-700 border-pink-200 dark:bg-pink-950/40 dark:text-pink-300 dark:border-pink-800',
    departmentCode: 'creative',
    departmentLabel: 'Creative & Marketing',
    services: [
      'Branding & Identity Design',
      'Logo Design & Editing',
      'Video Editing & Production',
    ],
  },
]

export const ALL_SERVICES: string[] = SERVICE_CATEGORIES.flatMap((c) => c.services)

export function getServiceCategory(serviceName: string): ServiceCategory | undefined {
  return SERVICE_CATEGORIES.find((cat) => cat.services.includes(serviceName))
}

export function getServiceBadgeClass(serviceName: string): string {
  const cat = getServiceCategory(serviceName)
  return (
    cat?.badgeClass ||
    'bg-primary/10 text-primary border-primary/20 dark:bg-primary/20 dark:text-primary dark:border-primary/30'
  )
}

export interface CategorizedServicesGroup {
  category: ServiceCategory
  services: string[]
}

/**
 * Groups an array of service names by their parent categories (TECHNOLOGY, MARKETING & GROWTH, CREATIVE).
 */
export function groupServicesByCategory(
  selectedServices?: string[] | null,
  masterData?: { name: string; category: string }[]
): CategorizedServicesGroup[] {
  if (!selectedServices || selectedServices.length === 0) return []
  const groups: CategorizedServicesGroup[] = []

  const masterDataMap: Record<string, string> = {}
  if (masterData) {
    for (const item of masterData) {
      if (item.name) {
        masterDataMap[item.name.toLowerCase()] = item.category === 'MARKETING_GROWTH' ? 'MARKETING & GROWTH' : item.category
      }
    }
  }

  for (const cat of SERVICE_CATEGORIES) {
    const matching = selectedServices.filter((s) => {
      const mappedCat = masterDataMap[s.toLowerCase()]
      if (mappedCat) {
        return mappedCat === cat.category
      }
      return cat.services.includes(s)
    })
    if (matching.length > 0) {
      groups.push({
        category: cat,
        services: matching,
      })
    }
  }

  // Handle any custom/legacy services not in standard list or master data
  const allKnown = groups.flatMap((g) => g.services)
  const unknown = selectedServices.filter((s) => !allKnown.includes(s))
  if (unknown.length > 0) {
    groups.push({
      category: {
        category: 'OTHER',
        color: '#64748b',
        badgeClass: 'bg-slate-500/10 text-slate-700 border-slate-200 dark:bg-slate-950/40 dark:text-slate-300 dark:border-slate-800',
        departmentCode: 'development',
        departmentLabel: 'Other',
        services: unknown,
      },
      services: unknown,
    })
  }

  return groups
}

/**
 * Department names (GoodDayDepartment.name) that deliver the given services,
 * e.g. ['Web Development', 'Branding & Identity Design'] -> ['Development', 'Creative & Marketing'].
 */
export function getDepartmentsForServices(services?: string[] | null): string[] {
  const names = new Set<string>()
  for (const service of services ?? []) {
    const category = getServiceCategory(service)?.category
    const department = category ? DEPARTMENT_BY_SERVICE_CATEGORY[category] : undefined
    if (department) names.add(department)
  }
  return Array.from(names)
}
